/**
 * User Explorer
 * 
 * ดูข้อมูล _User collection เพื่อเตรียมทำ Auth
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Users,
  RefreshCcw,
  Loader2,
  Search,
  Eye,
  ChevronLeft,
  ChevronRight,
  Copy,
  Shield,
  UserCheck,
  Key,
  Mail,
  Hash,
} from 'lucide-react';
import {
  queryClass,
  getClassSchema,
  getObjectById,
  PARSE_CONFIG,
} from '@/services/adapters/parse/parse-client';

interface UserData {
  objectId: string;
  username: string;
  email?: string;
  phone?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  status?: string;
  isActive?: boolean;
  emailVerified?: boolean;
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

export function UserExplorer() {
  // Schema state
  const [schema, setSchema] = useState<any>(null);
  const [schemaLoading, setSchemaLoading] = useState(false);
  
  // Users state
  const [users, setUsers] = useState<UserData[]>([]);
  const [userCount, setUserCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Selected user
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [showUserDialog, setShowUserDialog] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    admins: 0,
    verified: 0,
  });

  // Load schema on mount
  useEffect(() => {
    loadSchema();
    loadUsers(1);
  }, []);

  // Load _User schema
  const loadSchema = async () => {
    setSchemaLoading(true);
    try {
      const data = await getClassSchema('_User');
      setSchema(data);
      console.log('📋 _User Schema:', data);
    } catch (error) {
      console.error('Error loading schema:', error);
    } finally {
      setSchemaLoading(false);
    }
  };

  // Load users
  const loadUsers = async (pageNum: number = 1, search?: string) => {
    setLoading(true);
    try {
      const where: Record<string, any> = {};
      
      if (search) {
        // Search by username or email
        where['$or'] = [
          { username: { $regex: search, $options: 'i' } },
          { email: { $regex: search, $options: 'i' } },
          { phone: { $regex: search, $options: 'i' } },
        ];
      }
      
      const result = await queryClass('_User', {
        limit,
        skip: (pageNum - 1) * limit,
        order: '-createdAt',
        where: Object.keys(where).length > 0 ? where : undefined,
      });
      
      setUsers(result.results || []);
      setUserCount(result.count || 0);
      setPage(pageNum);
      
      // Calculate stats from first page (approximate)
      if (pageNum === 1 && !search) {
        const allUsers = result.results || [];
        setStats({
          total: result.count || 0,
          active: allUsers.filter((u: any) => u.isActive !== false && u.status !== 'inactive').length,
          admins: allUsers.filter((u: any) => u.role === 'admin' || u.isAdmin).length,
          verified: allUsers.filter((u: any) => u.emailVerified).length,
        });
      }
      
      console.log('👥 Users loaded:', result);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      loadUsers(1, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // View user detail
  const handleViewUser = async (objectId: string) => {
    try {
      const user = await getObjectById('_User', objectId);
      setSelectedUser(user);
      setShowUserDialog(true);
      console.log('👤 User detail:', user);
    } catch (error) {
      console.error('Error getting user:', error);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // Get field type color
  const getFieldTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      String: 'bg-blue-500/10 text-blue-500',
      Number: 'bg-green-500/10 text-green-500',
      Boolean: 'bg-amber-500/10 text-amber-500',
      Date: 'bg-purple-500/10 text-purple-500',
      Object: 'bg-cyan-500/10 text-cyan-500',
      Array: 'bg-pink-500/10 text-pink-500',
      Pointer: 'bg-orange-500/10 text-orange-500',
      Relation: 'bg-rose-500/10 text-rose-500',
    };
    return colors[type] || 'bg-muted';
  };

  // Format date
  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get user status badge
  const getUserStatusBadge = (user: UserData) => {
    if (user.status === 'inactive' || user.isActive === false) {
      return <Badge variant="secondary" className="bg-loss/10 text-loss border-0">Inactive</Badge>;
    }
    if (user.status === 'banned') {
      return <Badge variant="secondary" className="bg-loss/10 text-loss border-0">Banned</Badge>;
    }
    return <Badge variant="secondary" className="bg-win/10 text-win border-0">Active</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-primary" />
            User Explorer
          </h1>
          <p className="text-muted-foreground">ดูข้อมูล _User collection จาก Parse Server</p>
        </div>
        
        <Badge variant="secondary" className="gap-1">
          <Hash className="h-3 w-3" />
          {userCount} users
        </Badge>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Users</p>
                <p className="text-2xl font-bold">{stats.total.toLocaleString()}</p>
              </div>
              <Users className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-win">{stats.active}</p>
              </div>
              <UserCheck className="h-8 w-8 text-win" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold text-primary">{stats.admins}</p>
              </div>
              <Shield className="h-8 w-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Verified</p>
                <p className="text-2xl font-bold text-blue-500">{stats.verified}</p>
              </div>
              <Mail className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="users">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="schema">Schema</TabsTrigger>
            <TabsTrigger value="auth-fields">Auth Fields</TabsTrigger>
          </TabsList>
          
          <Button variant="outline" size="sm" onClick={() => loadUsers(page)} className="gap-2">
            <RefreshCcw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Users Tab */}
        <TabsContent value="users">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>User List</CardTitle>
                  <CardDescription>Showing {users.length} of {userCount} users</CardDescription>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search username, email, phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-72"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Username</TableHead>
                          <TableHead>Email / Phone</TableHead>
                          <TableHead>Role</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead className="w-12"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {users.map((user) => (
                          <TableRow key={user.objectId}>
                            <TableCell>
                              <div className="font-medium">{user.username}</div>
                              <div className="text-xs text-muted-foreground">{user.objectId}</div>
                            </TableCell>
                            <TableCell>
                              <div>{user.email || '-'}</div>
                              <div className="text-xs text-muted-foreground">{user.phone || ''}</div>
                            </TableCell>
                            <TableCell>
                              {user.role || user.isAdmin ? (
                                <Badge variant="secondary" className="bg-primary/10 text-primary border-0">
                                  {user.role || 'admin'}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">user</span>
                              )}
                            </TableCell>
                            <TableCell>{getUserStatusBadge(user)}</TableCell>
                            <TableCell className="text-muted-foreground text-sm">
                              {formatDate(user.createdAt)}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleViewUser(user.objectId)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                        {users.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                              No users found
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  
                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Page {page} of {Math.ceil(userCount / limit)}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadUsers(page - 1, searchTerm)}
                        disabled={page <= 1}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loadUsers(page + 1, searchTerm)}
                        disabled={page * limit >= userCount}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Schema Tab */}
        <TabsContent value="schema">
          <Card>
            <CardHeader>
              <CardTitle>_User Schema</CardTitle>
              <CardDescription>Field definitions from Parse Server</CardDescription>
            </CardHeader>
            <CardContent>
              {schemaLoading ? (
                <div className="flex items-center justify-center h-32">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : schema ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Field Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Target</TableHead>
                      <TableHead>Required</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {Object.entries(schema.fields || {}).map(([fieldName, fieldInfo]: [string, any]) => (
                      <TableRow key={fieldName}>
                        <TableCell>
                          <code className="text-sm bg-muted px-2 py-1 rounded">{fieldName}</code>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary" className={`border-0 ${getFieldTypeColor(fieldInfo.type)}`}>
                            {fieldInfo.type}
                          </Badge>
                        </TableCell>
                        <TableCell>{fieldInfo.targetClass || '-'}</TableCell>
                        <TableCell>{fieldInfo.required ? '✓' : '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-muted-foreground">No schema loaded</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auth Fields Tab - Analysis for Login */}
        <TabsContent value="auth-fields">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key className="h-5 w-5" />
                Authentication Fields Analysis
              </CardTitle>
              <CardDescription>Fields สำคัญสำหรับระบบ Login</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Login Fields */}
              <div>
                <h3 className="font-semibold mb-3">🔐 Login Fields</h3>
                <div className="grid gap-2">
                  {schema?.fields && (
                    <>
                      {['username', 'email', 'phone', 'password'].map((field) => (
                        <div key={field} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-2">
                            <code className="text-sm bg-background px-2 py-1 rounded">{field}</code>
                            {schema.fields[field] && (
                              <Badge variant="secondary" className={`border-0 ${getFieldTypeColor(schema.fields[field].type)}`}>
                                {schema.fields[field].type}
                              </Badge>
                            )}
                          </div>
                          {schema.fields[field] ? (
                            <Badge className="bg-win/10 text-win border-0">Available</Badge>
                          ) : (
                            <Badge className="bg-loss/10 text-loss border-0">Not Found</Badge>
                          )}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Role/Permission Fields */}
              <div>
                <h3 className="font-semibold mb-3">🛡️ Role & Permission Fields</h3>
                <div className="grid gap-2">
                  {schema?.fields && (
                    <>
                      {['role', 'roles', 'isAdmin', 'permissions', 'level', 'type', 'userType'].map((field) => (
                        schema.fields[field] && (
                          <div key={field} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <code className="text-sm bg-background px-2 py-1 rounded">{field}</code>
                              <Badge variant="secondary" className={`border-0 ${getFieldTypeColor(schema.fields[field].type)}`}>
                                {schema.fields[field].type}
                              </Badge>
                            </div>
                            <Badge className="bg-win/10 text-win border-0">Available</Badge>
                          </div>
                        )
                      ))}
                    </>
                  )}
                </div>
              </div>

              {/* Sample Login Code */}
              <div>
                <h3 className="font-semibold mb-3">📝 Sample Login Implementation</h3>
                <pre className="p-4 bg-muted rounded-lg text-sm overflow-x-auto">
{`// Login via Parse REST API
const login = async (username: string, password: string) => {
  const response = await fetch('${PARSE_CONFIG.serverURL}/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Parse-Application-Id': '${PARSE_CONFIG.appId.slice(0, 20)}...',
    },
    body: JSON.stringify({ username, password }),
  });
  
  if (!response.ok) {
    throw new Error('Login failed');
  }
  
  const user = await response.json();
  // Returns: { objectId, sessionToken, username, ... }
  return user;
};`}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Detail Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedUser?.username}
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              {/* Quick Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Object ID</div>
                  <div className="font-mono text-sm">{selectedUser?.objectId}</div>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <div className="text-xs text-muted-foreground mb-1">Session Token</div>
                  <div className="font-mono text-sm truncate">
                    {selectedUser?.sessionToken ? '***' + selectedUser.sessionToken.slice(-8) : '-'}
                  </div>
                </div>
              </div>
              
              {/* Full JSON */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Full Data (JSON)</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(JSON.stringify(selectedUser, null, 2))}
                    className="gap-1 h-7"
                  >
                    <Copy className="h-3 w-3" />
                    Copy
                  </Button>
                </div>
                <pre className="text-xs font-mono bg-muted p-4 rounded-lg overflow-x-auto">
                  {JSON.stringify(selectedUser, null, 2)}
                </pre>
              </div>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}








